import { getValidAccessToken, refreshAccessToken } from '../auth/token';

import { ApiError, ClientErrorCode, apiErrorFromProblem, isApiError, type ProblemDetails } from './errors';
import { api, buildUrl } from './http';
import {
  findPendingUpload,
  fingerprintOf,
  forgetPendingUpload as forgetStored,
  rememberPendingUpload as rememberStored,
} from './pendingUploads';
import type { FileRef } from './types';
import type { UploadOptions, UploadProgress } from './upload';

/*
 * Resumable (chunked) upload client for `POST /api/files/uploads` (docs/modules/files.md "Resumable uploads"). The file is
 * sent in chunks; whatever goes wrong with one chunk (the connection drops, a proxy times out, the server is restarting) is
 * retried with a growing pause after asking the server where the upload stands, so only the chunk in flight is sent again.
 * The session is remembered in the browser (pendingUploads.ts): after a page reload the same file picked again continues
 * where the server left off.
 */

/** The server's description of an upload (`UploadSessionDto`). */
interface SessionInfo {
  id: string;
  name: string;
  size: number;
  offset: number;
  chunkSize: number;
  minChunkSize: number;
  maxChunkSize: number;
  expiresAt: string;
  complete: boolean;
  file: FileRef | null;
}

/** What the upload is doing besides sending (shown next to the progress). */
export type UploadStatus =
  /** A chunk failed; the next attempt is number `attempt` (counting from 1), after `delayMs`. */
  | { kind: 'retrying'; attempt: number; delayMs: number }
  /** The browser is offline: the upload continues by itself when it is back. */
  | { kind: 'offline' }
  /** An earlier upload of this file was found: the first `offset` bytes are on the server already. */
  | { kind: 'resumed'; offset: number }
  /** Sending again. */
  | { kind: 'uploading' };

/** Tunables of the retry behaviour (module state so that tests can run it fast). */
export const resumableConfig = {
  /** Pause after the first failure; doubles with every further one. */
  baseDelayMs: 1000,
  maxDelayMs: 30_000,
  /** Failures in a row (while online) before the upload gives up; the session stays, so "retry" continues it. */
  maxFailures: 8,
  /** No progress for this long in one chunk request counts as a dropped connection. */
  stallMs: 60_000,
  /** Pause when the server says another request holds the chunk. */
  busyDelayMs: 1000,
  /** A new session after the old one vanished (swept, cancelled) at most this many times. */
  maxRestarts: 2,
};

const CHUNK_CONTENT_TYPE = 'application/offset+octet-stream';

function abortedError(): ApiError {
  return new ApiError({ status: 0, code: ClientErrorCode.aborted, message: 'Upload cancelled' });
}

function networkError(message = 'Network error'): ApiError {
  return new ApiError({ status: 0, code: ClientErrorCode.network, message });
}

interface ChunkResult {
  status: number;
  /** `Upload-Offset` of the answer. */
  offset: number | null;
  body: unknown;
}

/** Sends one chunk with progress; resolves with whatever status the server answered, rejects on transport failures. */
function sendChunk(
  url: string,
  token: string | null,
  offset: number,
  chunk: ArrayBuffer,
  onProgress: (loaded: number) => void,
  signal: AbortSignal | undefined,
): Promise<ChunkResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, resumableConfig.stallMs);
    };
    const onAbort = () => xhr.abort();
    const finish = () => {
      clearTimeout(stallTimer);
      signal?.removeEventListener('abort', onAbort);
    };

    xhr.open('PATCH', url);
    xhr.responseType = 'json';
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', CHUNK_CONTENT_TYPE);
    xhr.setRequestHeader('Upload-Offset', String(offset));
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.addEventListener('progress', (event) => {
      armStall();
      if (event.lengthComputable) onProgress(Math.min(event.loaded, chunk.byteLength));
    });
    xhr.addEventListener('load', () => {
      finish();
      const header = xhr.getResponseHeader('Upload-Offset');
      const parsed = header === null ? Number.NaN : Number(header);
      resolve({
        status: xhr.status,
        offset: Number.isFinite(parsed) ? parsed : null,
        body: xhr.response as unknown,
      });
    });
    xhr.addEventListener('error', () => {
      finish();
      reject(networkError());
    });
    xhr.addEventListener('timeout', () => {
      finish();
      reject(networkError('Timeout'));
    });
    xhr.addEventListener('abort', () => {
      finish();
      reject(stalled ? networkError('The connection stalled') : abortedError());
    });

    if (signal?.aborted) {
      finish();
      reject(abortedError());
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
    armStall();
    xhr.send(chunk);
  });
}

/** One chunk with a refresh of the access token when it has just run out. */
async function sendChunkAuthorized(
  url: string,
  offset: number,
  chunk: ArrayBuffer,
  onProgress: (loaded: number) => void,
  signal: AbortSignal | undefined,
): Promise<ChunkResult> {
  let result = await sendChunk(url, await getValidAccessToken(), offset, chunk, onProgress, signal);
  if (result.status === 401) {
    const token = await refreshAccessToken();
    if (token) result = await sendChunk(url, token, offset, chunk, onProgress, signal);
  }
  return result;
}

/** The bytes of a chunk; a file the browser can no longer read (moved, changed, permission revoked) is a final failure. */
async function readChunk(file: File, from: number, to: number): Promise<ArrayBuffer> {
  try {
    return await file.slice(from, to).arrayBuffer();
  } catch (cause) {
    throw new ApiError({
      status: 400,
      code: 'client.file_unreadable',
      message: 'The file cannot be read',
      cause,
    });
  }
}

function chunkError(result: ChunkResult, offset: number): ApiError {
  const problem =
    typeof result.body === 'object' && result.body !== null ? (result.body as ProblemDetails) : undefined;
  const error = apiErrorFromProblem(result.status, problem);
  // Attached for the retry logic: where the server says the upload stands.
  return Object.assign(error, { serverOffset: result.offset, sentAt: offset });
}

type Failure = 'retry' | 'resync' | 'busy' | 'restart' | 'fatal';

/** What to do after a failed request. */
function classify(error: unknown): Failure {
  if (!isApiError(error)) return 'retry';
  if (error.isAborted) return 'fatal';
  if (error.code === 'files.upload_offset_mismatch') return 'resync';
  if (error.code === 'files.upload_busy') return 'busy';
  if (
    error.code === 'files.upload_not_found' ||
    error.code === 'files.upload_expired' ||
    error.status === 410
  )
    return 'restart';
  if (error.status === 0 || error.status === 408 || error.status >= 500) return 'retry';
  // Rate limits answer without a files.* code; the portal's own refusals (quota, pending limit) are final.
  if (error.status === 429 && !error.code.startsWith('files.')) return 'retry';
  return 'fatal';
}

function backoffDelay(failures: number): number {
  const exponential = Math.min(resumableConfig.maxDelayMs, resumableConfig.baseDelayMs * 2 ** (failures - 1));
  // Jitter keeps many clients that lost the same server from coming back in the same second.
  return Math.round(exponential * (0.75 + Math.random() * 0.5));
}

function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortedError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortedError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Resolves when the browser is online (immediately when it is). */
function whenOnline(signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      resolve();
      return;
    }
    const cleanup = () => {
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
    };
    const onOnline = () => {
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      reject(abortedError());
    };
    window.addEventListener('online', onOnline);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Fingerprints of the files this page is uploading now: a second upload of the same file gets a session of its own. */
const active = new Set<string>();

/**
 * Uploads `file` in chunks and resolves with the stored file. Rejects with `ApiError`: `client.aborted` when cancelled
 * (the session is deleted at the server), the server's problem for refusals (too large, quota…), a network/5xx error once
 * the retries ran out (the session is kept: calling this again with the same file continues it).
 */
export async function resumableUpload(file: File, options: UploadOptions = {}): Promise<FileRef> {
  const fingerprint = fingerprintOf(file);
  // The same file attached twice at once (two chats, two folders) must not share one session — it would end as one stored
  // file used twice. Only the first upload continues and records the browser's saved session.
  const persist = !active.has(fingerprint);
  if (persist) active.add(fingerprint);
  try {
    return await upload(file, options, fingerprint, persist);
  } finally {
    if (persist) active.delete(fingerprint);
  }
}

async function upload(
  file: File,
  options: UploadOptions,
  fingerprint: string,
  persist: boolean,
): Promise<FileRef> {
  const { signal, onProgress, onStatus } = options;
  const findPending = () => (persist ? findPendingUpload(fingerprint) : undefined);
  const forgetPendingUpload = (key: string) => {
    if (persist) forgetStored(key);
  };
  const rememberPendingUpload = (entry: Parameters<typeof rememberStored>[0]) => {
    if (persist) rememberStored(entry);
  };
  const report = (loaded: number) => {
    const progress: UploadProgress = {
      loaded,
      total: file.size,
      percent: Math.min(100, Math.floor((loaded / file.size) * 100)),
    };
    onProgress?.(progress);
  };

  let session: SessionInfo | null = null;
  let offset = 0;
  let chunkSize = 0;
  let needsResync = false;
  let failures = 0;
  let resyncs = 0;
  let restarts = 0;
  let resumedReported = false;

  const sessionUrl = (id: string) => buildUrl(`/files/uploads/${id}`);

  const open = async (): Promise<SessionInfo> => {
    const pending = restarts === 0 ? findPending() : undefined;
    if (pending) {
      try {
        const known = await api.get<SessionInfo>(`/files/uploads/${pending.sessionId}`, { signal });
        if (known.size === file.size) return known;
      } catch (error) {
        const failure = classify(error);
        if (failure !== 'restart' && failure !== 'fatal') throw error;
        if (isApiError(error) && error.isAborted) throw error;
      }
      forgetPendingUpload(fingerprint);
    }
    const created = await api.post<SessionInfo>(
      '/files/uploads',
      { name: file.name, size: file.size, contentType: file.type || undefined },
      { signal },
    );
    return created;
  };

  const remember = (info: SessionInfo, stored: number) =>
    rememberPendingUpload({
      fingerprint,
      sessionId: info.id,
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      contentType: file.type,
      offset: stored,
      expiresAt: Date.parse(info.expiresAt),
      meta: options.resume?.meta,
    });

  const cancelAtServer = (info: SessionInfo | null) => {
    forgetPendingUpload(fingerprint);
    if (info) void api.delete(`/files/uploads/${info.id}`).catch(() => undefined);
  };

  report(0);
  for (;;) {
    if (signal?.aborted) {
      cancelAtServer(session);
      throw abortedError();
    }
    try {
      if (!session) {
        session = await open();
        if (session.complete && session.file) {
          forgetPendingUpload(fingerprint);
          report(file.size);
          return session.file;
        }
        offset = session.offset;
        chunkSize = session.chunkSize;
        remember(session, offset);
        if (offset > 0 && !resumedReported) {
          resumedReported = true;
          onStatus?.({ kind: 'resumed', offset });
        }
        report(offset);
        needsResync = false;
      } else if (needsResync) {
        const current = await api.get<SessionInfo>(`/files/uploads/${session.id}`, { signal });
        if (current.complete && current.file) {
          forgetPendingUpload(fingerprint);
          report(file.size);
          return current.file;
        }
        offset = current.offset;
        needsResync = false;
        report(offset);
      }

      onStatus?.({ kind: 'uploading' });
      const end = Math.min(file.size, offset + chunkSize);
      const sentFrom = offset;
      // Read before sending: a file that changed or vanished since it was chosen fails here, not half-way through a request.
      const chunk = await readChunk(file, sentFrom, end);
      const result = await sendChunkAuthorized(
        sessionUrl(session.id),
        sentFrom,
        chunk,
        (loaded) => report(sentFrom + loaded),
        signal,
      );

      if (result.status === 200) {
        forgetPendingUpload(fingerprint);
        report(file.size);
        return result.body as FileRef;
      }
      if (result.status !== 204) throw chunkError(result, sentFrom);

      failures = 0;
      offset = result.offset ?? end;
      // A good link earns the full chunk size back after it was cut down.
      chunkSize = Math.min(session.chunkSize, chunkSize * 2);
      remember(session, offset);
      report(offset);
    } catch (error) {
      if (signal?.aborted || (isApiError(error) && error.isAborted)) {
        cancelAtServer(session);
        throw abortedError();
      }
      const failure = classify(error);
      if (failure === 'fatal') {
        // Refused for good (too large, quota, not allowed): there is nothing to continue.
        forgetPendingUpload(fingerprint);
        if (session) void api.delete(`/files/uploads/${session.id}`).catch(() => undefined);
        throw error;
      }

      if (failure === 'restart') {
        forgetPendingUpload(fingerprint);
        session = null;
        offset = 0;
        restarts += 1;
        if (restarts > resumableConfig.maxRestarts) throw error;
        continue;
      }

      if (failure === 'resync') {
        resyncs += 1;
        if (resyncs > 25) throw error;
        // The server said where the upload stands; no pause needed.
        const serverOffset = (error as { serverOffset?: number | null }).serverOffset;
        if (typeof serverOffset === 'number') offset = serverOffset;
        else needsResync = true;
        continue;
      }

      if (failure === 'busy') {
        failures += 1;
        if (failures > resumableConfig.maxFailures) throw error;
        needsResync = true;
        onStatus?.({ kind: 'retrying', attempt: failures, delayMs: resumableConfig.busyDelayMs });
        await sleep(resumableConfig.busyDelayMs, signal).catch(() => undefined);
        continue;
      }

      // Transport trouble: wait for the network, pause, shrink the chunk, ask where the upload stands, send again.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        onStatus?.({ kind: 'offline' });
        try {
          await whenOnline(signal);
        } catch {
          cancelAtServer(session);
          throw abortedError();
        }
      } else {
        failures += 1;
        if (failures > resumableConfig.maxFailures) throw error;
      }
      const delay = backoffDelay(Math.max(1, failures));
      onStatus?.({ kind: 'retrying', attempt: Math.max(1, failures), delayMs: delay });
      try {
        await sleep(delay, signal);
      } catch {
        cancelAtServer(session);
        throw abortedError();
      }
      if (session) {
        chunkSize = Math.max(session.minChunkSize, Math.floor(chunkSize / 2));
        needsResync = true;
      }
    }
  }
}
